/* ================================================================
   FIGHT PH — Product Page JavaScript
   Handles variant selection, image gallery, quantity, and add-to-cart
   on the product detail template.
   Namespace: FightPH.product
   ================================================================ */

/* FightPH is declared in theme.js — reuse it (const can't be redeclared) */
window.FightPH = window.FightPH || {};

FightPH.product = (function () {
  'use strict';

  // ------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------
  let productData = null;       // Full Shopify product JSON
  let currentVariant = null;    // Currently selected variant object
  let currentImageIndex = 0;    // Gallery image index

  // ------------------------------------------------------------------
  // DOM references (populated in init)
  // ------------------------------------------------------------------
  const dom = {};

  // ------------------------------------------------------------------
  // Price formatting
  // Formats a price in cents to Philippine Peso: ₱1,234.00
  // ------------------------------------------------------------------
  const formatMoney = (cents) => {
    if (cents == null) return '';
    const amount = (parseInt(cents, 10) / 100).toLocaleString('en-PH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `\u20B1${amount}`;
  };

  // ------------------------------------------------------------------
  // Variant selector
  // Listens for clicks on option buttons rendered by Liquid in
  // sections/product-main.liquid. When an option changes:
  //   1. Updates active state on buttons
  //   2. Finds the matching variant from product JSON
  //   3. Updates price, image, SKU, availability, URL, hidden input
  // ------------------------------------------------------------------
  const variantSelector = {
    init() {
      const optionContainers = document.querySelectorAll('[data-option-index]');
      if (!optionContainers.length) return;

      optionContainers.forEach((container) => {
        const buttons = container.querySelectorAll('.product-detail__variant-btn');
        buttons.forEach((btn) => {
          btn.addEventListener('click', () => {
            // Update active state within this option group
            buttons.forEach((b) => b.classList.remove('product-detail__variant-btn--active'));
            btn.classList.add('product-detail__variant-btn--active');

            // Find and apply the new variant
            const newVariant = this.findVariant();
            if (newVariant) {
              currentVariant = newVariant;
              this.updateAll(newVariant);
            }
          });
        });
      });
    },

    /**
     * Read the currently selected options from the active buttons
     * and find the matching variant in the product JSON.
     */
    findVariant() {
      const selectedOptions = [];
      document.querySelectorAll('[data-option-index]').forEach((container) => {
        const active = container.querySelector('.product-detail__variant-btn--active');
        if (active) {
          selectedOptions.push(active.getAttribute('data-option-value'));
        }
      });

      if (!productData || !productData.variants) return null;

      return productData.variants.find((variant) => {
        return variant.options.every((opt, i) => opt === selectedOptions[i]);
      });
    },

    /**
     * Update all variant-dependent UI elements.
     */
    updateAll(variant) {
      this.updatePrice(variant);
      this.updateAddToCart(variant);
      this.updateBuyNow(variant);
      this.updateHiddenInput(variant);
      this.updateSku(variant);
      this.updateUrl(variant);
      this.updateImage(variant);
      this.updateOptionAvailability(variant);
    },

    /** Update the displayed price (sale + compare-at) */
    updatePrice(variant) {
      const priceEl = document.querySelector('[data-product-price]');
      if (!priceEl) return;

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceEl.innerHTML = `
          <span class="price__was">${formatMoney(variant.compare_at_price)}</span>
          <span class="price__sale">${formatMoney(variant.price)}</span>
        `;
      } else {
        priceEl.textContent = formatMoney(variant.price);
      }
    },

    /** Enable/disable add-to-cart button and update text */
    updateAddToCart(variant) {
      const btn = document.querySelector('[data-add-to-cart]');
      if (!btn) return;

      if (variant.available) {
        btn.disabled = false;
        btn.textContent = 'Add to Cart';
      } else {
        btn.disabled = true;
        btn.textContent = 'Sold Out';
      }
    },

    /** Enable/disable Buy Now button based on variant availability */
    updateBuyNow(variant) {
      const btn = document.querySelector('[data-buy-now]');
      if (!btn) return;

      if (variant && variant.available) {
        btn.disabled = false;
        btn.textContent = 'Buy Now';
      } else if (variant && !variant.available) {
        btn.disabled = true;
        btn.textContent = 'Sold Out';
      } else {
        btn.disabled = true;
        btn.textContent = 'Unavailable';
      }
    },

    /** Update the hidden variant ID input (used by the Shopify form) */
    updateHiddenInput(variant) {
      const input = document.querySelector('[data-variant-id]');
      if (input) input.value = variant.id;
    },

    /** Update the SKU display if present */
    updateSku(variant) {
      const skuEl = document.querySelector('[data-variant-sku]');
      if (skuEl) skuEl.textContent = variant.sku || '';
    },

    /** Update the browser URL with the variant parameter */
    updateUrl(variant) {
      if (!variant.id) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    },

    /**
     * If the variant has an associated image, switch the gallery
     * to display it.
     */
    updateImage(variant) {
      if (!variant.featured_image || !variant.featured_image.src) return;

      // Find the thumbnail that matches this variant's image
      const thumbs = document.querySelectorAll('[data-gallery-thumb-src]');
      thumbs.forEach((thumb, idx) => {
        const thumbSrc = thumb.getAttribute('data-gallery-thumb-src');
        // Compare by checking if the variant image URL is contained in the thumb src
        // (Shopify image URLs vary by size parameter)
        if (thumbSrc && variant.featured_image.src.includes(thumbSrc.split('?')[0].split('/').pop().split('_')[0])) {
          gallery.goTo(idx);
        }
      });
    },

    /**
     * Update option button availability based on currently selected options.
     * Disables option buttons that would result in an unavailable variant.
     */
    updateOptionAvailability(variant) {
      if (!productData || !productData.variants) return;

      // For each option group, check which values have at least one
      // available variant given the other selected options
      const optionContainers = document.querySelectorAll('[data-option-index]');
      const selectedOptions = [];
      optionContainers.forEach((container) => {
        const active = container.querySelector('.product-detail__variant-btn--active');
        if (active) {
          selectedOptions.push(active.getAttribute('data-option-value'));
        }
      });

      optionContainers.forEach((container, containerIndex) => {
        const buttons = container.querySelectorAll('.product-detail__variant-btn');
        buttons.forEach((btn) => {
          const value = btn.getAttribute('data-option-value');

          // Build a test selection with this value substituted
          const testOptions = [...selectedOptions];
          testOptions[containerIndex] = value;

          // Check if any variant matches and is available
          const hasAvailable = productData.variants.some((v) => {
            return v.options[containerIndex] === value && v.available;
          });

          if (hasAvailable) {
            btn.disabled = false;
            btn.classList.remove('product-detail__variant-btn--unavailable');
          } else {
            btn.disabled = true;
            btn.classList.add('product-detail__variant-btn--unavailable');
          }
        });
      });
    }
  };

  // ------------------------------------------------------------------
  // Image gallery
  // Handles thumbnail clicks, prev/next navigation arrows.
  // Relies on the DOM from snippets/product-gallery.liquid:
  //   [data-product-gallery]          — wrapper
  //   [data-gallery-main]             — main <img> element
  //   [data-gallery-thumb-index]      — thumbnail buttons
  //   [data-gallery-thumb-src]        — full-size src on each thumb
  //   [data-gallery-prev/next]        — arrow buttons
  // ------------------------------------------------------------------
  const gallery = {
    mainImage: null,
    thumbs: [],

    init() {
      this.mainImage = document.querySelector('[data-gallery-main]');
      this.thumbs = Array.from(document.querySelectorAll('[data-gallery-thumb-index]'));
      const prevBtn = document.querySelector('[data-gallery-prev]');
      const nextBtn = document.querySelector('[data-gallery-next]');

      if (!this.mainImage || !this.thumbs.length) return;

      // Thumbnail click
      this.thumbs.forEach((thumb, idx) => {
        thumb.addEventListener('click', () => this.goTo(idx));

        // Keyboard: Enter/Space are handled natively by <button>,
        // add arrow key navigation within the thumbnail strip
        thumb.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            const next = (idx + 1) % this.thumbs.length;
            this.thumbs[next]?.focus();
            this.goTo(next);
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            const prev = (idx - 1 + this.thumbs.length) % this.thumbs.length;
            this.thumbs[prev]?.focus();
            this.goTo(prev);
          }
        });
      });

      // Prev/Next arrow buttons
      prevBtn?.addEventListener('click', () => {
        const prev = (currentImageIndex - 1 + this.thumbs.length) % this.thumbs.length;
        this.goTo(prev);
      });

      nextBtn?.addEventListener('click', () => {
        const next = (currentImageIndex + 1) % this.thumbs.length;
        this.goTo(next);
      });
    },

    /**
     * Navigate to a specific image by index.
     * Updates the main image src, active thumbnail, and currentImageIndex.
     */
    goTo(idx) {
      if (idx < 0 || idx >= this.thumbs.length) return;

      currentImageIndex = idx;
      const thumb = this.thumbs[idx];
      const fullSrc = thumb.getAttribute('data-gallery-thumb-src');

      if (fullSrc && this.mainImage) {
        // Fade transition
        this.mainImage.style.opacity = '0';
        this.mainImage.addEventListener('load', () => {
          this.mainImage.style.opacity = '1';
        }, { once: true });
        this.mainImage.src = fullSrc;
        this.mainImage.srcset = ''; // Clear srcset so the new src takes effect
      }

      // Update active thumbnail
      this.thumbs.forEach((t) => t.classList.remove('product-detail__thumb--active'));
      thumb.classList.add('product-detail__thumb--active');

      // Scroll the active thumb into view within the thumbnail strip
      thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  };

  // ------------------------------------------------------------------
  // Quantity selector
  // Handles +/- buttons for the quantity input on the product form.
  // Relies on data attributes from sections/product-main.liquid:
  //   [data-qty-minus] — decrement button
  //   [data-qty-plus]  — increment button
  //   [data-qty-input] — number input
  // ------------------------------------------------------------------
  const quantity = {
    init() {
      const minusBtn = document.querySelector('[data-qty-minus]');
      const plusBtn = document.querySelector('[data-qty-plus]');
      const input = document.querySelector('[data-qty-input]');

      if (!input) return;

      minusBtn?.addEventListener('click', () => {
        const current = parseInt(input.value, 10) || 1;
        input.value = Math.max(1, current - 1);
      });

      plusBtn?.addEventListener('click', () => {
        const current = parseInt(input.value, 10) || 1;
        input.value = current + 1;
      });

      // Prevent invalid values on manual input
      input.addEventListener('change', () => {
        const val = parseInt(input.value, 10);
        if (isNaN(val) || val < 1) input.value = 1;
      });

      // Prevent non-numeric input
      input.addEventListener('keydown', (e) => {
        // Allow: backspace, delete, tab, escape, enter, arrows
        const allowed = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight'];
        if (allowed.includes(e.key)) return;
        // Allow digits 0-9
        if (e.key >= '0' && e.key <= '9') return;
        e.preventDefault();
      });
    }
  };

  // ------------------------------------------------------------------
  // Add to cart
  // Intercepts the product form submission and POSTs to Shopify's
  // /cart/add.js endpoint via AJAX. On success, updates the cart count
  // badge and shows user feedback. Falls back to native form submission
  // if the fetch fails.
  // ------------------------------------------------------------------
  const addToCart = {
    init() {
      const form = document.querySelector('[data-product-form]');
      if (!form) return;

      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const submitBtn = form.querySelector('[data-add-to-cart]');
        if (!submitBtn || submitBtn.disabled) return;

        // Get form data
        const variantId = form.querySelector('[data-variant-id]')?.value;
        const qty = form.querySelector('[data-qty-input]')?.value || 1;

        if (!variantId) {
          console.error('FightPH: No variant ID found');
          return;
        }

        // Disable button and show loading state
        const originalText = submitBtn.textContent;
        submitBtn.disabled = true;
        submitBtn.textContent = 'Adding...';

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: parseInt(variantId, 10),
              quantity: parseInt(qty, 10)
            })
          });

          if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            throw new Error(errorData.description || 'Could not add to cart');
          }

          // Success — update cart badge
          if (FightPH.theme?.cartBadge) {
            FightPH.theme.cartBadge.refresh();
          }

          // Show success feedback
          submitBtn.textContent = 'Added!';
          setTimeout(() => {
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          }, 2000);

        } catch (err) {
          console.error('FightPH: Add to cart error', err);

          // Show error feedback
          submitBtn.textContent = err.message || 'Error — try again';
          submitBtn.classList.add('product-detail__add-btn--error');
          setTimeout(() => {
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
            submitBtn.classList.remove('product-detail__add-btn--error');
          }, 3000);
        }
      });
    }
  };

  // ------------------------------------------------------------------
  // Load product JSON
  // Reads the product data from the <script type="application/json"
  // data-product-json> element rendered in sections/product-main.liquid.
  // ------------------------------------------------------------------
  const loadProductData = () => {
    const jsonEl = document.querySelector('[data-product-json]');
    if (!jsonEl) return null;

    try {
      return JSON.parse(jsonEl.textContent);
    } catch (err) {
      console.error('FightPH: Failed to parse product JSON', err);
      return null;
    }
  };

  // ------------------------------------------------------------------
  // Select initial variant
  // If the URL contains a ?variant= parameter, select that variant.
  // Otherwise, use the first available variant.
  // ------------------------------------------------------------------
  const selectInitialVariant = () => {
    if (!productData || !productData.variants) return null;

    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');

    if (variantId) {
      const urlVariant = productData.variants.find(
        (v) => String(v.id) === String(variantId)
      );
      if (urlVariant) return urlVariant;
    }

    // Fall back to the first available variant, or just the first variant
    return (
      productData.variants.find((v) => v.available) ||
      productData.variants[0] ||
      null
    );
  };

  // ------------------------------------------------------------------
  // Public API — exposed as FightPH.product
  // ------------------------------------------------------------------
  return {
    formatMoney,

    /** Initialize all product page modules. Called on DOMContentLoaded. */
    init() {
      // Only run on product pages
      const productSection = document.querySelector('.product-detail-section');
      if (!productSection) return;

      // Load product data from JSON script tag
      productData = loadProductData();
      if (!productData) {
        console.warn('FightPH: No product data found');
        return;
      }

      // Determine initial variant
      currentVariant = selectInitialVariant();

      // Initialize modules
      variantSelector.init();
      gallery.init();
      quantity.init();
      addToCart.init();

      // If URL specified a variant, update the UI to match
      if (currentVariant) {
        // Sync the active option buttons to match the initial variant
        const optionContainers = document.querySelectorAll('[data-option-index]');
        optionContainers.forEach((container, idx) => {
          const buttons = container.querySelectorAll('.product-detail__variant-btn');
          buttons.forEach((btn) => {
            const value = btn.getAttribute('data-option-value');
            if (value === currentVariant.options[idx]) {
              btn.classList.add('product-detail__variant-btn--active');
            } else {
              btn.classList.remove('product-detail__variant-btn--active');
            }
          });
        });
      }
    }
  };
})();

// ------------------------------------------------------------------
// Boot
// ------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
  FightPH.product.init();
});
